import React, {Fragment, useEffect, useState} from "react";
import {Button, Col, Drawer, Image, Input, Row, Tag} from "antd";
import {useTranslation} from "react-i18next";
import {extractLocalizedValue} from "../../../common";
import {ArrowRightOutlined} from "@ant-design/icons";
import {formatDateTime, useIsMobile} from "../../../../../_common/function";
import {apiGetQuestionnaireFormSetting} from "../../../../../api/restful/questionnaire";
import {VERIFICATION_API} from "../../../../../../config/microserviceApis";

const VerificationHistoryModal_3 = ({userVerificationTiers, viewingRecord, isViewOpen, handleClose}) => {
    const {t} = useTranslation();
    const url = VERIFICATION_API;
    const isMobile = useIsMobile()

    const [steps, setSteps] = useState([]);

    const selectedTier = userVerificationTiers?.find(d => d?._id === viewingRecord?.tierSetting?._id)

    const resolveDisplayName = doc => extractLocalizedValue(doc?.displayName) || doc?.name;

    const mapSteps = (steps = [], sourceSteps = []) =>
        steps.map(step => {
            const found = sourceSteps?.find(d => d?.sourceId === step?.sourceId)

            return {
                ...step,
                displayName: resolveDisplayName(found?.sourceDoc),
                sourceType: found?.sourceType,
                type: found?.sourceDoc?.type,
                questionnaireId: found?.questionnaireId,
                value:
                    found?.sourceType === "document_setting"
                        ? mapSteps(step?.value?.steps, found?.sourceDoc?.steps)
                        : step?.value,
            }
        })

    const formatDocuments = (viewingRecord, selectedTier) => {
        return viewingRecord?.documents?.map(doc => {
            const foundDoc = selectedTier?.documentSettings?.find(
                d => d?._id === doc._id
            )?.documentSettingDoc

            return {
                ...doc,
                displayName: resolveDisplayName(foundDoc),
                steps: mapSteps(doc?.steps, foundDoc?.steps),
            }
        })
    }

    const formatted = formatDocuments(viewingRecord, selectedTier)

    const getHeaderBreadcrumb = () => {
        const tier = extractLocalizedValue(viewingRecord?.tierSetting?.displayName) || viewingRecord?.tierSetting?.name

        let breadcrumbParts = tier ? [tier] : []

        let current = formatted?.find(d => d?._id === steps?.[0])
        if (current) breadcrumbParts.push(current?.displayName)

        for (let i = 1; i < steps.length; i++) {
            current = current?.steps?.find(s => s?.sourceId === steps[i])
            if (current) breadcrumbParts.push(current?.displayName)
        }

        return (
            <p>
                <span className={"secondary-text me-2 font-semibold"}>
                    {extractLocalizedValue(viewingRecord?.flowSetting?.displayName) || viewingRecord?.flowSetting?.name}
                    {breadcrumbParts?.length > 0 && " > "}
                </span>
                {breadcrumbParts.map((part, idx) => {
                    const isLast = idx === breadcrumbParts.length - 1;
                    return (
                        <span key={idx} className={isLast ? "" : "secondary-text me-2"}>
                                {part} {!isLast && " > "}
                            </span>
                    )
                })}
            </p>
        )
    }


    const DocumentCard = ({title, onClick}) => (
        <Col span={24}>
            <div className="history-doc-item" onClick={onClick} role="button">
                <span className="history-doc-title">{title}</span>
                <ArrowRightOutlined className="history-doc-arrow"/>
            </div>
        </Col>
    );

    const QuestionnaireFieldDisplay = ({step}) => {
        const [setting, setSetting] = useState(null);

        useEffect(() => {
            if (step?.questionnaireId) {
                apiGetQuestionnaireFormSetting(step.questionnaireId).then(res => {
                    if (res?.status === "success") setSetting(res.data);
                });
            }
        }, [step?.questionnaireId]);

        const handleView = () => {
            const theme = localStorage.getItem("color");
            const lang = localStorage.getItem("language");
            const questionnaireUrl = "https://" + window.location.hostname.replace("member", "questionnaire") + "/#/_public/forms/external/" + step.value + `?theme=${theme}&lang=${lang}`;
            window.open(questionnaireUrl, "_blank");
        };

        return (
            <Col xs={24} className="mb-3">
                <div className="history-questionnaire-row">
                    <div>
                        <div className="history-questionnaire-title">{setting?.title || step?.displayName}</div>
                        {setting?.description && (
                            <small className="history-questionnaire-label">{setting.description}</small>
                        )}
                    </div>
                    {step?.value && (
                        <Button onClick={handleView}>{t("common.view")}</Button>
                    )}
                </div>
            </Col>
        );
    };

    const FieldDisplay = ({step}) => (
        <Fragment key={step?.sourceId}>
            {step?.type === "questionnaire" ? (
                <QuestionnaireFieldDisplay step={step}/>
            ) : (
                <Col sm={24} md={12} className="mb-3">
                    <label className="history-field-label">{step?.displayName}</label>
                    {/* Liveness evidence is admin-only. The stored value is an asset
                        path, so show the outcome — never the path, never the frame. */}
                    {step?.type === "liveness_detection" ? (
                        <Tag color={step?.value ? "success" : "default"} bordered={false}>
                            {step?.value ? t("Verified") : t("Not verified")}
                        </Tag>
                    ) : step?.type === "file" && step?.value ? (
                        <Image src={`${url}/${step?.value}`} style={{borderRadius: 10}}/>
                    ) : step?.type === "date" && step?.value ? (
                        <Input className="history-field-input" value={formatDateTime(step?.value)} disabled/>
                    ) : (
                        <Input className="history-field-input" value={t(step?.value?.toString())} disabled/>
                    )}
                </Col>
            )}
            {step?.expiredAt &&
                <Col sm={24} md={12} className="mb-3">
                    <label
                        className="history-field-label">{step?.displayName} - {t("userKycField.expired_date")}</label>
                    <Input className="history-field-input" value={formatDateTime(step?.expiredAt)} disabled/>
                </Col>
            }
        </Fragment>
    )

    const renderStepsRecursive = (stepsList = [], onNavigate) => {
        return stepsList?.filter(doc => doc?.value?.length || doc?.sourceType === "field_setting")?.map(step =>
            step?.sourceType === "document_setting" ? (
                <DocumentCard
                    key={step?.sourceId}
                    title={step?.displayName}
                    onClick={() => onNavigate(step?.sourceId)}
                />
            ) : (
                <FieldDisplay key={step?.sourceId} step={step}/>
            )
        );
    };

    return (
        <>
            <Drawer
                destroyOnClose
                width={isMobile ? "100%" : "50%"}
                title={
                    <div className={"text-small"}>
                        {getHeaderBreadcrumb()}
                    </div>
                }
                open={isViewOpen}
                onClose={handleClose}
                footer={null}
                getContainer={() => document.getElementById("accountVerificationV2")}
            >
                <Row gutter={[16, 16]}>
                    {(() => {
                        if (steps.length === 0) {
                            return formatted?.map(doc =>
                                <DocumentCard
                                    key={doc?._id}
                                    title={doc?.displayName}
                                    onClick={() => setSteps([doc?._id])}
                                />
                            );
                        }

                        let current = formatted?.find(d => d?._id === steps[0]);
                        for (let i = 1; i < steps.length; i++) {
                            current = current?.steps?.find(s => s?.sourceId === steps[i]);
                        }

                        return renderStepsRecursive(current?.steps || current?.value, (id) =>
                            setSteps(prev => [...prev, id])
                        );
                    })()}
                </Row>

                {
                    steps?.length > 0 &&
                    <Button
                        className={"mt-6"}
                        block
                        type={"default"}
                        onClick={() => setSteps(prev => prev.slice(0, -1))}
                    >
                        {t("common.back")}
                    </Button>
                }
            </Drawer>
        </>
    )
}

export default VerificationHistoryModal_3;